/**
 * useAudioRecorder - React hook for browser audio recording.
 *
 * Captures microphone audio via MediaRecorder, tracks duration,
 * provides warnings before max duration, and auto-stops at the limit.
 *
 * @example
 * ```tsx
 * const { status, audioBlob, duration, startRecording, stopRecording, reset } =
 *   useAudioRecorder({ maxDurationSec: 600, warnAtSec: 180 });
 * ```
 */

import { useState, useRef, useCallback, useEffect } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Configuration options for useAudioRecorder. */
export interface AudioRecorderOptions {
  /** Maximum recording duration in seconds (default: 600 = 10 minutes). */
  maxDurationSec?: number;
  /** Show warning when duration reaches this many seconds (default: 180 = 3 minutes). */
  warnAtSec?: number;
  /** Preferred MIME type for recording (default: 'audio/webm;codecs=opus'). */
  mimeType?: string;
}

/** Status of the audio recorder. */
export type RecorderStatus = 'idle' | 'requesting' | 'recording' | 'stopped' | 'error';

/** Return value of the useAudioRecorder hook. */
export interface AudioRecorderReturn {
  /** Current recorder status. */
  status: RecorderStatus;
  /** The recorded audio blob (available after stopping). */
  audioBlob: Blob | null;
  /** Elapsed recording time in seconds. */
  duration: number;
  /** Error message if status is 'error'. */
  error: string | null;
  /** True when duration >= warnAtSec. */
  showWarning: boolean;
  /** The configured max duration in seconds. */
  maxDuration: number;
  /** Current audio level (0-1) for visualisation. Only valid while recording. */
  audioLevel: number;
  /** Active MediaStream from the microphone. Available while recording. */
  stream: MediaStream | null;
  /** Request mic permission and begin recording. */
  startRecording: () => Promise<void>;
  /** Stop recording and produce the audio blob. */
  stopRecording: () => void;
  /** Reset to idle, clearing blob and duration. */
  reset: () => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_MAX_DURATION_SEC = 600;
const DEFAULT_WARN_AT_SEC = 180;
const DEFAULT_MIME_TYPE = 'audio/webm;codecs=opus';
/** Fallback MIME types to try if the preferred one is not supported. */
const FALLBACK_MIME_TYPES = ['audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4', ''];

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * React hook for capturing audio from the user's microphone.
 *
 * @param options - Configuration for max duration, warning threshold, and MIME type.
 * @returns Object with recording state, controls, and the captured audio blob.
 */
export function useAudioRecorder(options: AudioRecorderOptions = {}): AudioRecorderReturn {
  const {
    maxDurationSec = DEFAULT_MAX_DURATION_SEC,
    warnAtSec = DEFAULT_WARN_AT_SEC,
    mimeType = DEFAULT_MIME_TYPE,
  } = options;

  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const durationRef = useRef(0);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const levelFrameRef = useRef<number | null>(null);

  /**
   * Selects a supported MIME type from the preferred type and fallbacks.
   *
   * @returns A supported MIME type string.
   */
  const selectMimeType = useCallback((): string => {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mimeType)) {
      return mimeType;
    }
    for (const fallback of FALLBACK_MIME_TYPES) {
      if (
        typeof MediaRecorder !== 'undefined' &&
        (fallback === '' || MediaRecorder.isTypeSupported(fallback))
      ) {
        return fallback;
      }
    }
    return '';
  }, [mimeType]);

  /**
   * Clears the duration timer interval.
   */
  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /**
   * Stops audio level monitoring.
   */
  const stopLevelMonitor = useCallback(() => {
    if (levelFrameRef.current !== null) {
      cancelAnimationFrame(levelFrameRef.current);
      levelFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevel(0);
  }, []);

  /**
   * Stops all media stream tracks.
   */
  const stopTracks = useCallback(() => {
    stopLevelMonitor();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, [stopLevelMonitor]);

  /**
   * Stops the recorder programmatically. Called by user or by auto-stop.
   */
  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === 'recording') {
      recorder.stop();
    }
    clearTimer();
  }, [clearTimer]);

  /**
   * Requests microphone access and begins recording.
   */
  const startRecording = useCallback(async () => {
    // Reset state
    setError(null);
    setAudioBlob(null);
    setDuration(0);
    durationRef.current = 0;
    chunksRef.current = [];
    setStatus('requesting');

    try {
      // Microphone API requires a secure context (HTTPS or localhost)
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error(
          'Microphone is not available. Your browser requires a secure (HTTPS) connection to access the microphone.',
        );
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Set up audio level monitoring via AnalyserNode
      try {
        const audioCtx = new AudioContext();
        audioContextRef.current = audioCtx;
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        analyserRef.current = analyser;

        const dataArray = new Uint8Array(analyser.fftSize);
        const updateLevel = () => {
          if (!analyserRef.current) return;
          // Use time-domain data for reliable voice amplitude detection
          analyserRef.current.getByteTimeDomainData(dataArray);
          // Calculate RMS deviation from silence (128 = silence baseline)
          let sumSquares = 0;
          for (let i = 0; i < dataArray.length; i++) {
            const deviation = (dataArray[i] - 128) / 128;
            sumSquares += deviation * deviation;
          }
          const rms = Math.sqrt(sumSquares / dataArray.length);
          // Scale up: typical speech RMS is 0.05-0.3, map to 0-1 range
          setAudioLevel(Math.min(rms * 4, 1));
          levelFrameRef.current = requestAnimationFrame(updateLevel);
        };
        levelFrameRef.current = requestAnimationFrame(updateLevel);
      } catch {
        // Audio level monitoring is non-essential; continue without it
      }

      const selectedMimeType = selectMimeType();
      const recorderOptions: MediaRecorderOptions = {};
      if (selectedMimeType) {
        recorderOptions.mimeType = selectedMimeType;
      }

      const recorder = new MediaRecorder(stream, recorderOptions);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const finalBlob = new Blob(chunksRef.current, {
          type: selectedMimeType || 'audio/webm',
        });
        setAudioBlob(finalBlob);
        setStatus('stopped');
        stopTracks();
      };

      recorder.onerror = (event: Event) => {
        const mediaError = event as MediaRecorderErrorEvent;
        setError(mediaError.error?.message ?? 'Recording error');
        setStatus('error');
        clearTimer();
        stopTracks();
      };

      // Start recording with 1-second timeslice for resilience
      recorder.start(1000);
      setStatus('recording');

      // Start duration timer
      timerRef.current = setInterval(() => {
        durationRef.current += 1;
        setDuration(durationRef.current);

        // Auto-stop at max duration
        if (durationRef.current >= maxDurationSec) {
          stopRecording();
        }
      }, 1000);
    } catch (err) {
      let message: string;
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        message =
          'Microphone access was denied. Please allow microphone access in your browser settings and try again.';
      } else if (err instanceof DOMException && err.name === 'NotFoundError') {
        message = 'No microphone found. Please connect a microphone and try again.';
      } else {
        message = err instanceof Error ? err.message : 'Failed to access microphone';
      }
      setError(message);
      setStatus('error');
      stopTracks();
    }
  }, [maxDurationSec, selectMimeType, stopRecording, clearTimer, stopTracks]);

  /**
   * Resets everything back to idle state.
   */
  const reset = useCallback(() => {
    stopRecording();
    stopTracks();
    clearTimer();
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    durationRef.current = 0;
    setStatus('idle');
    setAudioBlob(null);
    setDuration(0);
    setError(null);
    setAudioLevel(0);
  }, [stopRecording, stopTracks, clearTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimer();
      stopTracks();
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearTimer, stopTracks]);

  const showWarning = status === 'recording' && duration >= warnAtSec;

  return {
    status,
    audioBlob,
    duration,
    error,
    showWarning,
    maxDuration: maxDurationSec,
    audioLevel,
    stream: streamRef.current,
    startRecording,
    stopRecording,
    reset,
  };
}
